import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { FileSignature, Download, Trash2, X, MailCheck, MailX, MailWarning, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { OnboardingAPI } from '../api';

const fmtDate = (d) => (d?.month && d?.day && d?.year ? `${d.month}/${d.day}/${d.year}` : '—');
const fmtTs = (ts) => (ts ? new Date(ts).toLocaleString() : '—');
const EMAIL = {
  sent: { icon: MailCheck, cls: 'text-[#0AA24F]', text: 'Copy emailed' },
  failed: { icon: MailX, cls: 'text-[#E5387B]', text: 'Email failed' },
  not_configured: { icon: MailWarning, cls: 'text-[#B7791F]', text: 'Email not configured' },
};

const EmailBadge = ({ status }) => {
  const b = EMAIL[status];
  if (!b) return null;
  const Icon = b.icon;
  return <span className={`inline-flex items-center gap-1 text-[11px] ${b.cls}`} data-testid={`agreement-email-${status}`}><Icon className="w-3 h-3" /> {b.text}</span>;
};

const Detail = ({ id, onClose, onDeleted, readOnly }) => {
  const [sub, setSub] = useState(null);
  useEffect(() => { OnboardingAPI.submission(id).then(setSub).catch(() => toast.error('Failed to load agreement')); }, [id]);
  const remove = async () => {
    if (!window.confirm('Delete this agreement record permanently?')) return;
    try { await OnboardingAPI.remove(id); onDeleted(id); onClose(); toast.success('Agreement deleted'); } catch (e) { toast.error('Failed to delete'); }
  };
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={onClose}>
      <aside className="w-full max-w-[520px] h-full bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="agreement-detail">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E3E5F5]">
          <h3 className="text-[16px] font-bold text-[#0A1551]">Investor Agreement</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[#F0F1FB]" aria-label="Close" data-testid="agreement-detail-close"><X className="w-4 h-4 text-[#6F76A7]" /></button>
        </div>
        {!sub ? <div className="p-6 text-[13px] text-[#6F76A7]">Loading…</div> : (
          <div className="p-6 space-y-5 text-[14px]">
            <div>
              <div className="text-[20px] font-bold text-[#0A1551]" data-testid="agreement-detail-name">{sub.first_name} {sub.last_name}</div>
              <div className="text-[#6F76A7]">{sub.email} · {sub.phone}</div>
              <div className="mt-1"><EmailBadge status={sub.copy_email_status} /></div>
            </div>
            <dl className="grid grid-cols-2 gap-3 text-[13px]">
              <div className="bg-[#F5F6FB] rounded-lg p-3"><dt className="text-[#6F76A7]">Agreement date</dt><dd className="font-semibold text-[#0A1551]">{fmtDate(sub.date)}</dd></div>
              <div className="bg-[#F5F6FB] rounded-lg p-3"><dt className="text-[#6F76A7]">Submitted</dt><dd className="font-semibold text-[#0A1551]">{fmtTs(sub.submitted_at)}</dd></div>
              <div className="bg-[#F5F6FB] rounded-lg p-3"><dt className="text-[#6F76A7]">E-records consent</dt><dd className="font-semibold text-[#0A1551]">{sub.terms_intro ? 'Agreed' : '—'}</dd></div>
              <div className="bg-[#F5F6FB] rounded-lg p-3"><dt className="text-[#6F76A7]">Data processing consent</dt><dd className="font-semibold text-[#0A1551]">{sub.terms_data ? 'Agreed' : '—'}</dd></div>
            </dl>
            <div>
              <div className="text-[12px] font-semibold text-[#6F76A7] uppercase tracking-wide mb-2">Client signature</div>
              <div className="border border-[#E3E5F5] rounded-lg bg-[#FAFBFF] p-3">
                <img src={OnboardingAPI.signatureUrl(sub.id)} alt={`Signature of ${sub.first_name} ${sub.last_name}`} className="w-full max-h-[160px] object-contain" data-testid="agreement-signature-image" />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <a href={OnboardingAPI.pdfUrl(sub.id)} target="_blank" rel="noreferrer" data-testid="agreement-download-pdf" className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-[#0F766E] hover:bg-[#0B5F58] text-white text-[13px] font-semibold"><Download className="w-4 h-4" /> Download PDF</a>
              {!readOnly && (
              <button onClick={remove} data-testid="agreement-delete" className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#F5C2D3] text-[#E5387B] hover:bg-[#FEF2F2] text-[13px] font-medium"><Trash2 className="w-4 h-4" /> Delete</button>
            )}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
};

const AgreementsInbox = ({ readOnly = false }) => {
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q), 300); return () => clearTimeout(t); }, [q]);

  const filters = useMemo(() => ({ q: debouncedQ.trim() || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined }), [debouncedQ, dateFrom, dateTo]);
  const load = useCallback(() => OnboardingAPI.submissions(filters).then(setItems)
    .catch((e) => { setItems([]); toast.error(e?.response?.data?.detail || 'Failed to load agreements'); }), [filters]);
  useEffect(() => { load(); }, [load]);
  const hasFilters = !!(debouncedQ.trim() || dateFrom || dateTo);

  return (
    <div data-testid="agreements-inbox">
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <p className="text-[13px] text-[#6F76A7]">Completed investor agreements from the public onboarding form.</p>
          <div className="flex items-center gap-3">
            {!readOnly && (
              <a href={OnboardingAPI.csvUrl(filters)} data-testid="agreements-export-csv" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#D5D7ED] bg-white text-[13px] font-semibold text-[#0A1551] hover:bg-[#F5F6FB]">
                <Download className="w-3.5 h-3.5" /> Export CSV
              </a>
            )}
            <button onClick={load} className="inline-flex items-center gap-1.5 text-[13px] text-[#0A1551] hover:underline" data-testid="agreements-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-[#6F76A7] absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or phone" data-testid="agreements-search"
              className="w-full pl-9 pr-3 py-2 border border-[#D5D7ED] rounded-md text-[14px] text-[#0A1551] bg-white focus:outline-none focus:border-[#0A1551]" />
          </div>
          <div className="flex items-center gap-2">
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="agreements-date-from" aria-label="From date"
              className="px-3 py-2 border border-[#D5D7ED] rounded-md text-[13px] text-[#0A1551] bg-white focus:outline-none focus:border-[#0A1551]" />
            <span className="text-[#9599C0] text-[13px]">to</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="agreements-date-to" aria-label="To date"
              className="px-3 py-2 border border-[#D5D7ED] rounded-md text-[13px] text-[#0A1551] bg-white focus:outline-none focus:border-[#0A1551]" />
            {hasFilters && (
              <button onClick={() => { setQ(''); setDateFrom(''); setDateTo(''); }} data-testid="agreements-clear-filters"
                className="inline-flex items-center gap-1 px-2.5 py-2 rounded-md text-[13px] text-[#E5387B] hover:bg-[#FEF2F2]"><X className="w-3.5 h-3.5" /> Clear</button>
            )}
          </div>
        </div>
        {items && hasFilters && <p className="text-[12px] text-[#6F76A7]" data-testid="agreements-result-count">{items.length} matching agreement{items.length === 1 ? '' : 's'}</p>}
      </div>
      {items === null ? <div className="text-[13px] text-[#6F76A7]">Loading…</div> : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="agreements-empty">
          <div className="w-24 h-24 rounded-full bg-[#F0F1FB] flex items-center justify-center mb-4"><FileSignature className="w-10 h-10 text-[#B0B4D0]" strokeWidth={1.5} /></div>
          <h3 className="text-[18px] font-semibold text-[#0A1551]">No agreements yet</h3>
          <p className="text-[14px] text-[#6F76A7] mt-1">Signed agreements from the landing page will appear here.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E3E5F5] rounded-xl overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-2.5 bg-[#F9FAFD] border-b border-[#E3E5F5] text-[11px] font-semibold text-[#6F76A7] uppercase tracking-wide">
            <div className="col-span-4">Client</div><div className="col-span-3">Contact</div><div className="col-span-2">Agreement date</div><div className="col-span-2">Submitted</div><div className="col-span-1 text-right">PDF</div>
          </div>
          {items.map((s) => (
            <div key={s.id} onClick={() => setOpenId(s.id)} className="grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-4 md:items-center px-4 py-3 border-b border-[#E3E5F5] last:border-0 hover:bg-[#F9FAFD] cursor-pointer text-[13px]" data-testid={`agreement-row-${s.id}`}>
              <div className="md:col-span-4 min-w-0">
                <div className="font-semibold text-[#0A1551] truncate">{s.first_name} {s.last_name}</div>
                <EmailBadge status={s.copy_email_status} />
              </div>
              <div className="md:col-span-3 min-w-0 text-[#4A517D]"><div className="truncate">{s.email}</div><div className="text-[12px] text-[#6F76A7]">{s.phone}</div></div>
              <div className="md:col-span-2 text-[#4A517D]"><span className="md:hidden text-[#9599C0]">Signed: </span>{fmtDate(s.date)}</div>
              <div className="md:col-span-2 text-[#6F76A7]"><span className="md:hidden text-[#9599C0]">Submitted: </span>{fmtTs(s.submitted_at)}</div>
              <div className="md:col-span-1 md:text-right mt-1 md:mt-0">
                <a href={OnboardingAPI.pdfUrl(s.id)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 p-1.5 rounded hover:bg-[#E6F4F2] text-[#0F766E] text-[12px] font-semibold" aria-label="Download PDF" data-testid={`agreement-pdf-${s.id}`}><Download className="w-4 h-4" /><span className="md:hidden">PDF</span></a>
              </div>
            </div>
          ))}
        </div>
      )}
      {openId && <Detail id={openId} readOnly={readOnly} onClose={() => setOpenId(null)} onDeleted={(id) => setItems((prev) => prev.filter((x) => x.id !== id))} />}
    </div>
  );
};

export default AgreementsInbox;
